import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";

const AccountAdd = ({ open, onClose, token }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { handleAdd, loading, resetForm } = useAdd("shop/accounts", token);
  const {
    handleAddAsync: handleAddCurrencyAsync,
    loading: currencyCreateLoading,
  } = useAdd("shop/currencies", token);
  const selectedBranch = auth?.user?.selected_branch
    ? String(auth.user.selected_branch)
    : auth?.user?.user?.selected_branch
      ? String(auth.user.user.selected_branch)
      : "";

  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shop"],
    "shop/shop",
    token
  );

  const shops = useMemo(
    () => (Array.isArray(shopsData) ? shopsData : shopsData?.results || []),
    [shopsData]
  );
  const hasSingleShop = shops.length === 1;

  const [selectedShop, setSelectedShop] = useState("");
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currencies", "shop-account-add", selectedShop],
    selectedShop
      ? `shop/currencies/?page_size=all&shop=${selectedShop}`
      : "",
    token
  );

  const currencies = Array.isArray(currenciesData)
    ? currenciesData
    : currenciesData?.results || [];

  const [name, setName] = useState("");
  const [accountType, setAccountType] = useState("normal");
  const [currencyId, setCurrencyId] = useState("");
  const [openingBalance, setOpeningBalance] = useState("0");
  const [note, setNote] = useState("");
  const [openCurrencyDialog, setOpenCurrencyDialog] = useState(false);
  const [newCurrencyTitle, setNewCurrencyTitle] = useState("");

  useEffect(() => {
    if (shopLoading || shops.length === 0) return;

    const branchShop = selectedBranch
      ? shops.find((shop) => String(shop.id) === selectedBranch)
      : null;

    if (branchShop) {
      setSelectedShop(String(branchShop.id));
    } else if (hasSingleShop) {
      setSelectedShop(String(shops[0].id));
    } else if (!selectedShop) {
      setSelectedShop(String(shops[0].id));
    }
  }, [hasSingleShop, selectedBranch, selectedShop, shopLoading, shops]);

  useEffect(() => {
    setCurrencyId("");
  }, [selectedShop]);

  const handleQuickCurrencySave = async () => {
    const title = newCurrencyTitle.trim();
    if (!title) return;

    try {
      const createdCurrency = await handleAddCurrencyAsync({
        title,
        description: "",
      });
      if (createdCurrency?.id) {
        setCurrencyId(String(createdCurrency.id));
      }
      setNewCurrencyTitle("");
      setOpenCurrencyDialog(false);
    } catch {
      // The hook already shows the backend error toast.
    }
  };

  const handleSubmit = () => {
    handleAdd({
      shop: selectedShop,
      name: name.trim(),
      account_type: accountType,
      currency: currencyId,
      opening_balance: openingBalance,
      note: note.trim() || null,
    });
  };

  useEffect(() => {
    if (resetForm) {
      if (selectedBranch) {
        setSelectedShop(selectedBranch);
      } else if (hasSingleShop) {
        setSelectedShop(String(shops[0].id));
      } else if (shops.length) {
        setSelectedShop(String(shops[0].id));
      } else {
        setSelectedShop("");
      }
      setName("");
      setAccountType("normal");
      setCurrencyId("");
      setOpeningBalance("0");
      setNote("");
      onClose();
    }
  }, [resetForm, onClose, hasSingleShop, selectedBranch, shops]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("account.addAccount")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label={t("account.name")}
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <TextField
            select
            margin="dense"
            label={t("account.currency")}
            fullWidth
            required
            disabled={!selectedShop || currencyLoading}
            value={currencyId}
            onChange={(e) => {
              if (e.target.value === "__add_currency__") {
                setOpenCurrencyDialog(true);
                return;
              }
              setCurrencyId(e.target.value);
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    maxHeight: 320,
                  },
                },
              },
            }}
          >
            <MenuItem
              value="__add_currency__"
              sx={{
                color: "success.main",
                fontWeight: 700,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              {t("currency.addCurrency", "Add Currency")}
            </MenuItem>
            {!selectedShop ? (
              <MenuItem value="">{t("account.selectShopFirst")}</MenuItem>
            ) : currencyLoading ? (
              <MenuItem value="" disabled>
                <CircularProgress size={20} />
              </MenuItem>
            ) : currencies.length === 0 ? (
              <MenuItem value="" disabled>
                {t("account.noCurrencies")}
              </MenuItem>
            ) : (
              currencies.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.title}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("account.openingBalance")}
            type="number"
            inputProps={{ step: "0.01" }}
            fullWidth
            value={openingBalance}
            onChange={(e) => setOpeningBalance(e.target.value)}
          />

          <TextField
            margin="dense"
            label={t("account.note")}
            fullWidth
            multiline
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />

          <TextField
            select
            margin="dense"
            label={t("account.type")}
            fullWidth
            required
            value={accountType}
            onChange={(e) => setAccountType(e.target.value)}
          >
            <MenuItem value="normal">{t("account.normal", "Normal")}</MenuItem>
            <MenuItem value="sarap">{t("account.sarap", "Sarap")}</MenuItem>
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("account.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            shopLoading ||
            !selectedShop ||
            !name.trim() ||
            !currencyId ||
            !accountType
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("account.submit")
          )}
        </Button>
      </DialogActions>

      <Dialog
        open={openCurrencyDialog}
        onClose={() => {
          if (currencyCreateLoading) return;
          setOpenCurrencyDialog(false);
          setNewCurrencyTitle("");
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t("currency.addCurrency", "Add Currency")}</DialogTitle>
        <DialogContent>
          <Box component="form" noValidate sx={{ mt: 1 }}>
            <TextField
              autoFocus
              margin="dense"
              label={t("currency.title")}
              fullWidth
              required
              value={newCurrencyTitle}
              onChange={(e) => setNewCurrencyTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleQuickCurrencySave();
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (currencyCreateLoading) return;
              setOpenCurrencyDialog(false);
              setNewCurrencyTitle("");
            }}
            color="secondary"
            disabled={currencyCreateLoading}
          >
            {t("currency.cancel")}
          </Button>
          <Button
            onClick={handleQuickCurrencySave}
            variant="contained"
            color="primary"
            disabled={currencyCreateLoading || !newCurrencyTitle.trim()}
          >
            {currencyCreateLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t("currency.submit")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AccountAdd;
